<template>
    <MainContainer>
        <div class="noten">
            <h2>
                Noten
                <b-button v-if="checkRight('write')" :size="size" @click="dataCreate">
                    Neu
                    <font-awesome-icon icon="plus" />
                </b-button>
            </h2>
            <Table
                bordered
                striped
                small
                showFilter
                :fields="fields"
                :items="items"
                :isBusy="busy"
                tableClass="main-table"
                sortBy="werk"
                busyText="Lädt Noten..."
                emptyText="Keine Noten gefunden"
            >
                <template v-slot:detail="{row: row}">
                    <b-button
                        :size="size"
                        variant="secondary"
                        @click="showDetails(row)"
                        class="mv-table-action-btn mr-1 mv-nowrap"
                    >
                        Details
                        <font-awesome-icon
                            :icon="row.item._showDetails ? 'caret-down' : 'caret-right'"
                            class="ml-1"
                        />
                    </b-button>
                </template>

                <template v-slot:row-details="{row: row}">
                    <NotenartDetail :id="row.item.id" :refresh="notenartDetailRefresh" />
                </template>

                <template v-slot:actions="{ row: row }">
                    <b-button
                        v-if="checkRight('write')"
                        :size="size"
                        variant="link"
                        @click="dataSelected(row)"
                        class="mv-table-action-btn mr-1"
                    >Bearbeiten</b-button>
                    <b-button
                        v-if="checkRight('delete') && !row.has_transaction"
                        :size="size"
                        @click="dataDelete(row)"
                        variant="link"
                        class="text-danger mv-table-action-btn"
                    >Löschen</b-button>
                </template>
            </Table>
            <EditModal
                :title="selected.title"
                :busy="selected.busy"
                size="xl"
                id="newModal"
                buttonText="Anlegen"
                scrollable
                @ok="dataProvide"
                @hide="dataHide"
            >
                <template v-if="selected.data">
                    <FormGroupBuilder
                        :fields="ioFields.new"
                        :data="selected.data"
                        :refresh="validationRefresh"
                    />
                </template>
            </EditModal>
            <EditModal
                :title="selected.title"
                :busy="selected.busy"
                size="xl"
                buttonText="Speichern"
                scrollable
                @ok="dataProvide"
                @hide="dataHide"
            >
                <b-tabs pills card vertical>
                    <b-tab title="Stammdaten" active>
                        <template v-if="selected.data">
                            <FormGroupBuilder
                                :fields="ioFields.edit"
                                :data="selected.data"
                                :refresh="validationRefresh"
                            />
                        </template>
                    </b-tab>
                    <b-tab title="Zuordnungen">
                        <template v-if="selected.data">
                            <Notenart
                                :id="selected.data.id"
                                :validation="notenartValidation"
                                @update="updateNotenart"
                            />
                        </template>
                    </b-tab>
                </b-tabs>
            </EditModal>
            <RemoveModal
                :title="selected.title"
                :busy="selected.busy"
                @ok="dataProvide"
                @hide="dataHide"
            />
        </div>
    </MainContainer>
</template>

<script>
import CommonView from "@/mixins/CommonView/CommonView";
import Size from "@/mixins/Size/Size";
import Validation from "@/mixins/Validation/Validation";
import { ApiFactory } from "@/api/ApiFactory.js";
import MainContainer from "@/components/MainContainer/MainContainer";
import Table from "@/components/Table/Table";
import EditModal from "@/components/EditModal/EditModal";
import RemoveModal from "@/components/RemoveModal/RemoveModal";
import FormGroupBuilder from "@/components/FormGroupBuilder/FormGroupBuilder";
import Notenart from "@/components/Notenart/Notenart";
import NotenartDetail from "@/components/Notenart/NotenartDetail";

import "./Noten.scss";

const NotenverwaltungApi = ApiFactory.get("notenverwaltung");

export default {
    name: "noten",
    mixins: [CommonView, Size, Validation],
    components: {
        MainContainer,
        Table,
        EditModal,
        RemoveModal,
        FormGroupBuilder,
        Notenart,
        NotenartDetail
    },
    data() {
        return {
            fields: [
                {
                    key: "id",
                    label: "ID",
                    sortable: true
                },
                {
                    key: "detail",
                    label: "Details"
                },
                {
                    key: "werk",
                    label: "Werk",
                    sortable: true
                },
                {
                    key: "komponist",
                    label: "Komponist",
                    sortable: true
                },
                {
                    key: "ausgabe",
                    label: "Ausgabe",
                    sortable: true
                },
                {
                    key: "actions",
                    label: "Funktionen",
                    class: "text-center"
                }
            ],
            ioFields: {
                new: [
                    {
                        type: "notice",
                        text:
                            "Wenn eine neue Note eingetragen wird, müssen zunächst die Stammdaten übermittelt werden, bevor einzelne Ausgaben oder Notenarten hinzugefügt werden können. Nach Erstellen der Note kann das dann über 'Bearbeiten' gemacht werden.",
                        variant: "warning"
                    },
                    {
                        key: "id",
                        label: "ID",
                        type: "text",
                        disabled: true,
                        oneWay: true
                    },
                    {
                        key: "werk",
                        label: "Werk",
                        type: "text",
                        mandatory: true
                    },
                    {
                        key: "komponist",
                        label: "Komponist",
                        type: "text",
                        mandatory: true
                    },
                    {
                        key: "ausgabe",
                        label: "Ausgabe",
                        type: "text",
                        mandatory: false
                    }
                ],
                edit: [
                    {
                        key: "id",
                        label: "ID",
                        type: "text",
                        disabled: true,
                        oneWay: true
                    },
                    {
                        key: "werk",
                        label: "Werk",
                        type: "text",
                        mandatory: true
                    },
                    {
                        key: "komponist",
                        label: "Komponist",
                        type: "text",
                        mandatory: true
                    },
                    {
                        key: "ausgabe",
                        label: "Ausgabe",
                        type: "text",
                        mandatory: false
                    }
                ]
            },
            api: NotenverwaltungApi,
            notenart: [],
            notenartDetailRefresh: false,
            notenartValidation: null
        };
    },
    methods: {
        process(item) {
            item._showDetails = false;
            return item;
        },
        async additionalRequests(selectedItem) {
            const response = await NotenverwaltungApi.putNotenart(
                selectedItem.id,
                this.notenart
            );
            if (!response.ok && response.json.validation) {
                this.notenartValidation = response.json.validation;
            } else {
                this.notenartValidation = null;
            }
            return response.ok;
        },
        fetchErrorHandling(response) {
            if (this.selected.action === "new") {
                this.handleValidationData(response, this.ioFields.new);
            } else {
                this.handleValidationData(response, this.ioFields.edit);
            }
        },
        postDataSet() {
            this.notenart = [];
            this.cleanValidationData(this.ioFields.new);
            this.cleanValidationData(this.ioFields.edit);
        },
        dataCreate() {
            const newObj = {};
            this.dataSet(newObj, "new", "Neu erstellen");
            this.$root.$emit("bv::show::modal", "newModal");
        },
        postProcessEdit(selectedItem, response) {
            this.notenartDetailRefresh = !this.notenartDetailRefresh;
            return selectedItem;
        },
        showDetails(row) {
            row.item._showDetails = !row.item._showDetails;
        },
        updateNotenart(value) {
            this.notenart = value;
        }
    }
};
</script>
